import {
  ApolloQueryResult,
  InternalRefetchQueriesInclude,
  useEnhancedLazyQuery,
} from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Exact, ProductIdTypeEnum } from "__generated__/globalTypes";
import { PurchaseCompleteHandler } from "components/Cart/CheckoutModal";
import styles from "components/Subscriptions/SubscriptionProduct.module.css";
import {
  StripeCustomerPortalUrlQuery,
  StripeCustomerPortalUrlVariables,
  SubscriptionProductQueryNode,
  SubscriptionUserQueryQuery,
} from "components/Subscriptions/__generated__/queries";
import {
  getBillingWording,
  SubscriptionBilling,
  SubscriptionInterval,
} from "components/Subscriptions/subscriptionUtils";
import WrappedButton from "components/common/Button";
import CouponDiscount from "components/common/CouponDiscount";
import Link from "components/common/Link";
import Loading, { LoadingButton } from "components/common/Loading";
import ProductPurchaseModal from "components/common/PurchaseModal/ProductPurchaseModal";
import { formatStartDay } from "components/events/utils";
import {
  SUBSCRIPTION_AUTO_RENEWAL_TEXT,
  SUBSCRIPTION_NAME_LOWER,
  SUBSCRIPTION_SUBSCRIBE,
} from "constants/site";
import { MEMBERS_PAGE_URL } from "constants/urls";
import { formatUSD } from "helpers/strings";
import { CURRENT_USER_QUERY } from "hooks";
import { CombinedSubscriptionProduct, STRIPE_CUSTOMER_PORTAL_URL_QUERY } from "./queries";

type Variation = SubscriptionProductQueryNode;

type SubscriptionSidebarProps = {
  product: CombinedSubscriptionProduct;
  refetch: (
    variables?: Partial<Exact<{ id: string; idType: ProductIdTypeEnum }>> | undefined
  ) => Promise<ApolloQueryResult<SubscriptionUserQueryQuery>>;
};

type ActiveSubscriptionProps = {
  subscription: NonNullable<CombinedSubscriptionProduct["userSubscription"]>;
};

const ActiveSubscription = ({ subscription }: ActiveSubscriptionProps) => {
  const currentUrl = window.location.href;

  const [getStripeUrl] = useEnhancedLazyQuery<
    StripeCustomerPortalUrlQuery,
    StripeCustomerPortalUrlVariables
  >(STRIPE_CUSTOMER_PORTAL_URL_QUERY);

  const [isRedirecting, setIsRedirecting] = useState(false);

  const getStripeUrlAndRedirect = async () => {
    setIsRedirecting(true);
    const result = await getStripeUrl({
      variables: {
        returnUrl: currentUrl,
      },
    });

    if (result.data?.stripeCustomerPortalUrl) {
      window.location.href = result.data.stripeCustomerPortalUrl;
    }
  };

  return (
    <>
      <p className={styles.subscriptionSidebarHeading}>Your {SUBSCRIPTION_NAME_LOWER}</p>
      {subscription.isAutoRenewing ? (
        <>
          <p className={styles.subscriptionStatusLabel}>Auto-renewing</p>
          <p className={styles.subscriptionStatusValue}>{formatStartDay(subscription.accessTo)}</p>
        </>
      ) : (
        <>
          <p className={styles.subscriptionStatusLabel}>Expires</p>
          <p className={styles.subscriptionStatusValue}>{formatStartDay(subscription.accessTo)}</p>
        </>
      )}
      <p className={styles.subscriptionManageLink}>
        <WrappedButton theme="secondary" disabled={isRedirecting} onClick={getStripeUrlAndRedirect}>
          {isRedirecting ? <LoadingButton text="Redirecting..." /> : "Manage"}
        </WrappedButton>
      </p>
      {subscription.membersPages && subscription.membersPages.length > 0 && (
        <div className={styles.membersPages}>
          {subscription.membersPages.map(page => (
            <Link
              key={page.id}
              className={styles.subscriptionPageLink}
              href={makeUrl(MEMBERS_PAGE_URL, { slug: page.slug })}
            >
              {page.title}
            </Link>
          ))}
        </div>
      )}
      {subscription.features && subscription.features.length > 0 && (
        <div className={styles.features}>
          {subscription.features.map(feature => (
            <Fragment key={feature.id}>
              {!!feature.ceLimit && feature.ceLimit > 0 && (
                <p className={styles.subscriptionFeature}>
                  You have redeemed {feature.ceCount} of {feature.ceLimit} available CEs
                </p>
              )}
            </Fragment>
          ))}
        </div>
      )}
    </>
  );
};

const SubscriptionPurchase = ({ product, refetch }: SubscriptionSidebarProps) => {
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [hasJustPurchased, setHasJustPurchased] = useState(false);
  const [awaitActivation, setAwaitActivation] = useState(false);
  const [variation, setVariation] = useState<Variation | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [billing, setBilling] = useState<SubscriptionBilling | null>(null);

  const onClickPurchase = () => setPaymentModalOpen(true);
  const onCloseModal = () => setPaymentModalOpen(false);

  const modalTitle = SUBSCRIPTION_SUBSCRIBE;
  const type = "subscription";
  const canPurchaseWithCredits = false;
  const creditCost = 0;

  const refetchQueries: InternalRefetchQueriesInclude = useMemo(() => {
    return [{ query: CURRENT_USER_QUERY }];
  }, []);

  useEffect(() => {
    if (product.__typename === "VariableProduct") {
      if (product.variations?.edges?.length) {
        const v = product.variations?.edges?.[0]?.node || null;

        if (!v) {
          return;
        }

        const interval = (v.attributes?.nodes?.find(
          a => a?.name === "pa_billing_interval" && ["MONTH", "YEAR"].includes(a?.value || "")
        )?.value || "UNKNOWN") as SubscriptionInterval;

        const intervalCount =
          Number(v.attributes?.nodes?.find(a => a?.name === "pa_billing_interval_count")?.value) ||
          0;

        setVariation(v);
        setPrice(Number(v.price?.substring(1)));
        setBilling({
          interval,
          intervalCount,
          wording: getBillingWording(interval, intervalCount),
        });
      }
    }
  }, [product]);

  const purchaseCompleteHandler: PurchaseCompleteHandler = info => {
    setHasJustPurchased(true);

    if (info?.free) {
      setAwaitActivation(true);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (awaitActivation) {
      interval = setInterval(() => {
        refetch();
      }, 2000);
    }

    // This component will unmount after the refetch loads the active subscription
    return () => {
      clearInterval(interval);
    };
  }, [awaitActivation, refetch]);

  if (hasJustPurchased || !variation) {
    return <Loading />;
  }

  return (
    <>
      <ProductPurchaseModal
        productId={product.id}
        productVariationId={variation.id}
        productDatabaseId={product.databaseId}
        productName={product.name}
        productPrice={price || undefined}
        subscriptionBilling={billing || undefined}
        hasPurchased={Boolean(product.userIsSubscribed)}
        canPurchaseWithCredits={canPurchaseWithCredits}
        creditCost={creditCost}
        type={type}
        open={paymentModalOpen}
        onRequestClose={onCloseModal}
        title={modalTitle}
        refetchQueries={refetchQueries}
        onPurchaseComplete={purchaseCompleteHandler}
      />

      <div className="text-3xl font-bold mt-4">{formatUSD(Number(price))}</div>
      {billing && <div className="text-xl font-bold mt-2">{billing.wording}</div>}

      {product.couponForUser && <CouponDiscount couponForUser={product.couponForUser} />}

      <WrappedButton className="my-4 w-full" theme="primary" onClick={onClickPurchase}>
        {SUBSCRIPTION_SUBSCRIBE}
      </WrappedButton>

      {billing && <div className="text-center">{SUBSCRIPTION_AUTO_RENEWAL_TEXT}</div>}
    </>
  );
};

const SubscriptionSidebar: React.FC<SubscriptionSidebarProps> = ({ product, refetch }) => {
  if (product.userIsSubscribed) {
    if (product.userSubscription) {
      return <ActiveSubscription subscription={product.userSubscription} />;
    }
    return <>Loading sub details...</>;
  }

  return <SubscriptionPurchase product={product} refetch={refetch} />;
};

export default SubscriptionSidebar;
